import { eventBus } from './service-worker-channel';
import { fetchWithRetry } from '@/utils';

export const entityTypeToConfigMap = {
  account: { title: 'Accounts', tableName: 'accounts', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  company: { title: 'Companies', tableName: 'companies', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  equipment: { title: 'Equipments', tableName: 'equipments', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  location: { title: 'Locations', tableName: 'locations', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  person: { title: 'Persons', tableName: 'persons', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  personLang: { title: 'Person Langs', tableName: 'person-langs', keyField: '_id', idField: 'uid', indexes: ['uid', 'personId'] },
  vehicle: { title: 'Vehicles', tableName: 'vehicles', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  vehicleContact: {
    title: 'Vehicle Contacts',
    tableName: 'vehicle-contacts',
    keyField: '_id',
    idField: 'uid',
    indexes: ['uid', 'vehicleId'],
  },
  vehicleEquipment: {
    title: 'Vehicle Equipments',
    tableName: 'vehicle-equipments',
    keyField: '_id',
    idField: 'uid',
    indexes: ['uid', 'vehicleId'],
  },
  vehicleReservation: {
    title: 'Vehicle Reservations',
    tableName: 'vehicle-reservations',
    keyField: '_id',
    idField: 'uid',
    indexes: ['uid', 'vehicleId'],
  },
  vehicleType: { title: 'Vehicle Types', tableName: 'vehicle-types', keyField: '_id', idField: 'uid', indexes: ['uid'] },
  chat: { title: 'Chats', tableName: 'chats', keyField: '_id', idField: 'id', indexes: ['id', 'personId'] },
};

const needToSync = [];

export const dbPromise = idb.openDB('app', 3, {
  upgrade(db, oldVersion, newVersion, tx) {
    for (const type of Object.keys(entityTypeToConfigMap)) {
      const typeInfo = entityTypeToConfigMap[type];
      if (!db.objectStoreNames.contains(typeInfo.tableName)) {
        db.createObjectStore(typeInfo.tableName, { keyPath: typeInfo.keyField });
        needToSync.push(type);
      }

      if (typeInfo.indexes) {
        const store = tx.objectStore(typeInfo.tableName);

        for (const index of typeInfo.indexes) {
          if (!store.indexNames.contains(index)) {
            store.createIndex(index, index, { unique: false });
          }
        }

        for (const index of store.indexNames) {
          if (!typeInfo.indexes.includes(index)) {
            store.deleteIndex(index);
          }
        }
      }
    }

    if (!db.objectStoreNames.contains('meta')) {
      db.createObjectStore('meta'); // holds the cursor
    }
  },
});

export async function getCursor() {
  return (await (await dbPromise).get('meta', 'cursor')) ?? null;
}

async function applyBatch(changes, newCursor) {
  const db = await dbPromise;
  const entityTypes = [...new Set(changes.map(c => c.type))];
  const tables = entityTypes.map(t => entityTypeToConfigMap[t]?.tableName).filter(Boolean);
  const tx = db.transaction([...tables, 'meta'], 'readwrite');

  const deletedItems = [];

  const ops = changes.map(async c => {
    const typeInfo = entityTypeToConfigMap[c.type];
    if (!typeInfo) {
      console.error('Unknown entity type in change:', c.type);
      return Promise.resolve();
    }

    const store = tx.objectStore(typeInfo.tableName);

    if (c.action === 'deleted') {
      deletedItems.push({ id: c.id, data: (await store.get(c.id)) ?? null });
    }

    return c.action === 'deleted' ? store.delete(c.id) : store.put(c.data);
  });

  await Promise.all([
    ...ops,
    tx.objectStore('meta').put(newCursor, 'cursor'),
    tx.done, // <- always include in the Promise.all
  ]);

  return deletedItems;
}

///////////////////////////

const channel = new BroadcastChannel('updates');
channel.onmessage = event => {
  const { type } = event.data;

  if (type === 'entity-changed') {
    const { type, entityType, entityId } = event.data.data;
    onEntityChanged(type, entityType, entityId);
  } else if (type === 'sync-start') {
    onSyncStart(event.data.entityType);
  } else if (type === 'sync-progress') {
    onSyncProgress(event.data.entityType, event.data.synced);
  } else if (type === 'sync-stop') {
    onSyncStop(event.data.entityType);
  } else if (type === 'sync-all-start') {
    onSyncAllStart();
  } else if (type === 'sync-all-stop') {
    onSyncAllStop();
  }
};

let polling = false;

export const startPolling = () => {
  polling = true;
  navigator.locks.request('updates-leader', () => new Promise(() => pollLoop()));
};

export const stopPolling = () => {
  polling = false;
};

async function pollLoop() {
  let cursor = await getCursor(); // resume point survives leader handoff

  for (const type of needToSync) {
    await syncEntities(type);
  }

  while (polling) {
    try {
      const response = await fetch('/api/v2/updates?cursor=' + encodeURIComponent(cursor ?? ''));

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error('API ' + data.error);
      }

      const result = data.result;
      if (!result.success) {
        continue;
      }

      const { changes, cursor: next } = result;

      if (changes?.length) {
        const deletedItems = await applyBatch(changes, next);

        cursor = next;

        for (const change of changes) {
          const typeInfo = entityTypeToConfigMap[change.type] ?? { idField: 'id' };

          const entityType = change.type;
          let type;
          let entityId;

          if (change.action === 'created') {
            type = 'entity-created';
            entityId = change.data[typeInfo.idField];
          } else if (change.action === 'updated') {
            type = 'entity-updated';
            entityId = change.data[typeInfo.idField];
          } else if (change.action === 'deleted') {
            type = 'entity-deleted';
            entityId = deletedItems.find(i => i.id === change.id)?.data[typeInfo.idField] ?? null;
          }

          channel.postMessage({ type: 'entity-changed', data: { type, entityType, entityId } });
          onEntityChanged(type, entityType, entityId);
        }
      } else if (next && next !== cursor) {
        await applyBatch([], next); // heartbeat: advance cursor only
        cursor = next;
      }
    } catch (error) {
      console.error(error);
      await new Promise(r => setTimeout(r, 1000)); // backoff, retry same cursor
    }
  }
}

let syncScreenRemoveTimeout = null;

async function onEntityChanged(type, entityType, entityId) {
  eventBus.dispatchEvent(new CustomEvent('notification', { detail: { type, entityType, entityId } }));
}

async function onSyncStart(entityType) {
  if (syncScreenRemoveTimeout) {
    clearTimeout(syncScreenRemoveTimeout);
    syncScreenRemoveTimeout = null;
  }

  let syncing = document.querySelector('#syncing');
  if (!syncing) {
    syncing = document.createElement('div');
    syncing.id = 'syncing';
    window.document.body.appendChild(syncing);
  }

  syncing.innerHTML = `Syncing ${entityTypeToConfigMap[entityType]?.title}...`;
}

async function onSyncProgress(entityType, synced) {
  let syncing = document.querySelector('#syncing');
  if (!syncing) {
    syncing = document.createElement('div');
    syncing.id = 'syncing';
    window.document.body.appendChild(syncing);
  }

  syncing.innerHTML = `Syncing ${entityTypeToConfigMap[entityType]?.title}... (${synced} items synced)`;
}

async function onSyncStop(entityType) {
  let syncing = document.querySelector('#syncing');
  if (!syncing) {
    syncing = document.createElement('div');
    syncing.id = 'syncing';
    window.document.body.appendChild(syncing);
  }

  syncing.innerHTML = `Sync completed for ${entityTypeToConfigMap[entityType]?.title}`;

  setTimeout(() => {
    const syncing = document.querySelector('#syncing');
    if (syncing) {
      window.document.body.removeChild(syncing);
    }
  }, 500);
}

export async function syncEntities(entityType) {
  channel.postMessage({ type: 'sync-start', entityType });
  onSyncStart(entityType);

  const typeInfo = entityTypeToConfigMap[entityType];
  if (!typeInfo) {
    console.error('Unknown entity type:', entityType);
    return;
  }

  const db = await dbPromise;
  const tx = db.transaction([typeInfo.tableName], 'readwrite');
  tx.objectStore(typeInfo.tableName).clear();
  await tx.done;

  const take = 100;
  let skip = 0;

  let entities = [];

  do {
    const response = await fetchWithRetry(`/api/v2/list?entityType=${entityType}&take=${take}&skip=${skip}`);
    if (!response.ok) {
      console.error('Failed to fetch entities:', response.statusText);
      break;
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch entities');
    }

    entities = data.result;

    const db = await dbPromise;
    const tx = db.transaction([typeInfo.tableName], 'readwrite');

    await Promise.all(entities.map(entity => tx.objectStore(typeInfo.tableName).put(entity)));

    skip += entities.length;

    channel.postMessage({ type: 'sync-progress', entityType, synced: skip });
    onSyncProgress(entityType, skip);
  } while (entities.length === take);

  channel.postMessage({ type: 'sync-stop', entityType });
  onSyncStop(entityType);
}
